import { useEffect, useState } from 'react'
import { doc, getDoc } from 'firebase/firestore'
import { db } from '@/lib/firebase'
import { COLLECTIONS, type PublicSummary } from '@shared/model'
import { formatSek } from '@/lib/format'
import { useFeature } from '@/lib/features'
import { cn } from '@/lib/cn'

/**
 * Vad häftet är värt, som en stämpel på inloggningsrutans hörn.
 *
 * Den som står på inloggningssidan har antingen fått en kod av en granne
 * eller sett en affisch, och frågan hen har är vad hen får för pengarna.
 * Utan svar är inloggningen en dörr utan skylt.
 *
 * Summan avrundas nedåt till hel hundralapp. Dels läser "ungefär 5 400 kr"
 * som en uppskattning och inte som ett löfte, dels ska vi hellre lova för
 * lite än för mycket: den som räknar efter i butiken ska bli glad, inte
 * besviken.
 *
 * Siffran hämtas ur `publicSummary`, det enda dokumentet i databasen som går
 * att läsa utloggad. Erbjudandena själva ligger kvar bakom inloggning – de är
 * varan.
 */

/** Under den här summan säger siffran mer om att data saknas än om häftet. */
const MINIMUM_SEK = 500

/**
 * Det som ska stå, eller null när det inte finns något att säga.
 *
 * Egen funktion för att adminytans provknapp ska visa exakt samma sak. Byggde
 * båda sin egen text skulle de glida isär vid första ändringen, och då är
 * provknappen värre än ingen alls: den visar något annat än verkligheten.
 */
export function bookletValueSentence(
  summary: Pick<PublicSummary, 'offerCount' | 'totalValueSek'> | null,
): { count: string; amount: string } | null {
  if (!summary) return null

  const rounded = Math.floor(summary.totalValueSek / 100) * 100
  if (rounded < MINIMUM_SEK || summary.offerCount <= 0) return null

  return {
    count: `${summary.offerCount} rabatter`,
    amount: `ca ${formatSek(rounded)}`,
  }
}

/**
 * Själva stämpeln.
 *
 * Fylld och inte konturerad, till skillnad från UsedStamp. Den sitter snett
 * över rutans övre högra hörn och ligger därför till hälften på det vita
 * kortet och till hälften på den blå bottnen – en konturstämpel i en enda
 * färg hade varit oläslig på den ena eller den andra. Fylld bär den sin egen
 * bakgrund och fungerar mot båda.
 *
 * Lirablått med gul text är klubbens par och ger 12,4:1. Gult som liten fylld
 * bricka mot blått är precis den användning designreglerna tillåter – det är
 * gult som brödtext mot ljust som är förbjudet.
 *
 * Den vita ringen gör att den läser som pålagd i efterhand, inte som en del
 * av kortet. Det är hela poängen med en stämpel.
 */
export function BookletValueStamp({
  summary,
  className,
}: {
  summary: Pick<PublicSummary, 'offerCount' | 'totalValueSek'> | null
  className?: string
}) {
  const text = bookletValueSentence(summary)
  if (!text) return null

  return (
    <span
      className={cn(
        'pointer-events-none absolute -top-4 -right-3 z-10 rotate-[8deg]',
        'flex flex-col items-center rounded-xl bg-brand px-3 py-1.5',
        'ring-2 ring-white/90 shadow-card',
        className,
      )}
    >
      <span className="font-display text-[10px] leading-none font-bold tracking-[0.14em] text-on-brand/80 uppercase">
        {text.count}
      </span>
      <span className="font-display text-base leading-tight font-extrabold text-accent">
        {text.amount}
      </span>
    </span>
  )
}

/** Hämtar sammanfattningen och sätter stämpeln, när funktionen är påslagen. */
export function BookletValueNote() {
  const [summary, setSummary] = useState<PublicSummary | null>(null)
  const enabled = useFeature('loginValue')

  useEffect(() => {
    let cancelled = false

    void getDoc(doc(db, COLLECTIONS.publicSummary, 'current')).then(
      (snapshot) => {
        if (!cancelled && snapshot.exists()) setSummary(snapshot.data() as PublicSummary)
      },
      () => {
        // Offline eller ingen sammanfattning ännu. Stämpeln är en trevlighet,
        // inte något inloggningen hänger på – då säger vi hellre ingenting.
      },
    )

    return () => {
      cancelled = true
    }
  }, [])

  /*
   * Ingen reserverad höjd längre.
   *
   * Raden låg tidigare i flödet under rubriken och behövde plats även när den
   * var tom, annars hoppade formuläret nedåt när summan landade. Som absolut
   * placerad stämpel tar den ingen plats alls, och kan därför dyka upp när
   * den vill utan att flytta något.
   */
  return enabled ? <BookletValueStamp summary={summary} /> : null
}
